import {
    useEffect,
    useState
} from "react";
import {
    TouchableOpacity,
    Platform,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";
import {
    DownloadIcon
} from "lucide-react-native";

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: Array<string>;
    readonly userChoice: Promise<{
        outcome: "accepted" | "dismissed",
        platform: string
    }>;
    prompt(): Promise<void>;
}

const InstallAppBanner = () => {
    const [
        deferredPrompt,
        setDeferredPrompt
    ] = useState<BeforeInstallPromptEvent | null>(null);

    const [
        isInstalled,
        setIsInstalled
    ] = useState(false);

    const {
        radiuses,
        colors,
        spaces,
        borders
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    useEffect(() => {
        if (Platform.OS === "web") {
            const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as unknown as { standalone?: boolean }).standalone || document.referrer.includes("android-app://");
            setIsInstalled(!!isStandalone);

            const handleBeforeInstallPrompt = (e: Event) => {
                e.preventDefault();
                setDeferredPrompt(e as BeforeInstallPromptEvent);
            };

            if (window.deferredPWAInstallPrompt) {
                setDeferredPrompt(window.deferredPWAInstallPrompt as BeforeInstallPromptEvent);
            }

            window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

            return () => {
                window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            };
        } else {
            setIsInstalled(true);
            return () => {};
        }
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const {
                outcome
            } = await deferredPrompt.userChoice;
            if (outcome === "accepted") {
                setDeferredPrompt(null);
            }
        }
    };

    if (isInstalled || Platform.OS !== "web" || !deferredPrompt) {
        return null;
    }

    return <View
        style={[
            stylesheet.container,
            {
                borderColor: colors.content.border.subtle,
                borderWidth: borders.line,
                borderRadius: radiuses.md,
                padding: spaces.spacingMd,
                marginBottom: spaces.spacingLg
            }
        ]}
    >
        <View
            style={stylesheet.leftContainer}
        >
            <View
                style={stylesheet.iconContainer}
            >
                <DownloadIcon
                    color={colors.content.text.high}
                    size={24}
                />
            </View>
            <View
                style={stylesheet.textContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="bodyLargeSize"
                    style={[
                        {
                            marginBottom: spaces.spacingSm / 2
                        }
                    ]}
                >
                    {localize("install-app")}
                </Text>
                <Text
                    customColor={colors.content.text.low}
                    variant="bodyMediumSize"
                >
                    {localize("install-app-desc")}
                </Text>
            </View>
        </View>
        <TouchableOpacity
            onPress={() => {
                handleInstallClick();
            }}
            style={[
                stylesheet.button,
                {
                    backgroundColor: colors.content.text.high,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingSm,
                    borderRadius: radiuses.sm
                }
            ]}
        >
            <Text
                color="onPrimary"
                variant="bodyMediumSize"
            >
                {localize("install")}
            </Text>
        </TouchableOpacity>
    </View>;
};
export default InstallAppBanner;
